import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Text, Button, neutral } from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { Link } from "@mui/material";
import { QuoteConfirmationDialog } from "@pages/settings/subscription-settings/credits/quote-confirmation-dialog";
import { QuoteSuccessDialog } from "@pages/settings/subscription-settings/credits/quote-success-dialog";
import { SettingsTabs } from "@router/route-params";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { appStateSelector } from "@store/app/app-selector";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppSelector } from "@store/store-helper";
import { formatStripeDisplayPrice } from "@utils/stripe-product-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useState } from "react";

interface Props {
  /** The credit package to request quote for */
  selectedPackage: FaroCoreApiTypes.StripeProduct | undefined;
}

/** Renders request quote for credit package */
export function RequestCreditQuote({
  selectedPackage,
}: Props): JSX.Element | undefined {
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const companyId = useCompanyId();
  const { navigateToSettingsPage } = useAppNavigation();
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowConfirmationDialog, setShouldShowConfirmationDialog] =
    useState(false);
  const [shouldShowSuccessDialog, setShouldShowSuccessDialog] = useState(false);

  /**
   * Handles the request to get a quote for the selected credit package.
   */
  const handleRequestCreditQuote = useCallback(async () => {
    if (!selectedPackage) {
      return;
    }

    setIsLoading(true);

    trackEvent({
      name: WorkspaceEvents.requestQuote,
      props: {
        creditsAmount: selectedPackage.tokenAmount,
      },
    });

    try {
      // Create a message for the notification that contains the selected package details
      const customMessage = `Requesting quote for:
          • Package: ${selectedPackage.name}
          • Price: ${formatStripeDisplayPrice(selectedPackage.price.price)}`;

      await faroCoreApiClient.notification.notificationRequest({
        payload: {
          type: "service_request",
          context: { companyId },
          data: {
            name: `Requesting to add ${selectedPackage.tokenAmount} ${CREDITS_DISPLAY_NAME}`,
            comment: customMessage,
          },
        },
      });

      setShouldShowConfirmationDialog(false);
      setShouldShowSuccessDialog(true);
    } catch (error) {
      handleErrorWithToast({
        id: `sendQuoteRequest-${Date.now().toString()}`,
        title: "Request failed. Please try again.",
        error,
      });
    }
  }, [selectedPackage, trackEvent, companyId, handleErrorWithToast]);

  function onConfirmationDialogClose(): void {
    setShouldShowSuccessDialog(false);

    navigateToSettingsPage({
      companyId,
      settingsTab: SettingsTabs.subscription,
    });
  }

  return (
    <>
      {!isAlphaTestingEnabled ? (
        // TODO: This is temporary change and will be reverted when stripe payment will be enabled
        // https://faro01.atlassian.net/browse/ST-3105
        <SphereTooltip
          title="Please select a package to request a quote."
          shouldShowTooltip={!selectedPackage}
        >
          <Button
            fullWidth
            disabled={!selectedPackage}
            onClick={() => setShouldShowConfirmationDialog(!!selectedPackage)}
          >
            Request a quote
          </Button>
        </SphereTooltip>
      ) : (
        <Text
          variant="bodyM"
          sx={{
            marginTop: "18px",
            marginX: "auto",
          }}
        >
          You may{" "}
          <Link
            fontWeight="600"
            color={neutral[800]}
            onClick={() => setShouldShowConfirmationDialog(!!selectedPackage)}
            sx={{ cursor: "pointer", display: "inline-block" }}
          >
            <SphereTooltip
              title="Please select a package to request a quote"
              shouldShowTooltip={!selectedPackage}
            >
              request a quote
            </SphereTooltip>
          </Link>{" "}
          as an alternative.
        </Text>
      )}

      {selectedPackage && shouldShowConfirmationDialog && (
        <QuoteConfirmationDialog
          selectedPackage={selectedPackage}
          isLoading={isLoading}
          onConfirm={handleRequestCreditQuote}
          onClose={() => setShouldShowConfirmationDialog(false)}
        />
      )}

      {shouldShowSuccessDialog && (
        <QuoteSuccessDialog onClose={onConfirmationDialogClose} />
      )}
    </>
  );
}
